// Single source of truth for locale-aware number handling.
//
// The app targets European users but the active UI language is stored in
// localStorage under the "lang" key (see translationHelper.js / topbar).
// English => US number format (dot decimal, comma grouping).
// Every other supported language => its European format (comma decimal).
//
// Built entirely on Intl.NumberFormat so we never hardcode separators.

const LANG_TO_LOCALE = {
  en: "en-US",
  fr: "fr-FR",
  de: "de-DE",
  nl: "nl-BE",
};

const DEFAULT_LANG = "fr";

export function getActiveLang() {
  try {
    return localStorage.getItem("lang") || DEFAULT_LANG;
  } catch (e) {
    return DEFAULT_LANG;
  }
}

export function getActiveLocale() {
  return LANG_TO_LOCALE[getActiveLang()] || LANG_TO_LOCALE[DEFAULT_LANG];
}

// Derive the real grouping/decimal separators for a locale.
export function getSeparators(locale = getActiveLocale()) {
  const parts = new Intl.NumberFormat(locale).formatToParts(11111.1);
  const group = parts.find((p) => p.type === "group");
  const decimal = parts.find((p) => p.type === "decimal");
  return {
    group: group ? group.value : ",",
    decimal: decimal ? decimal.value : ".",
  };
}

// Whitespace that locales (notably fr-FR) use as a grouping separator:
// regular space, no-break space (U+00A0), narrow no-break space (U+202F).
const SPACE_RE = /[\s  ]/g;

function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Strict per-locale parse -> canonical Number (or null when empty/invalid).
// Grouping separators are stripped; the locale decimal separator becomes ".".
export function parseLocaleNumber(input, locale = getActiveLocale()) {
  if (typeof input === "number") return Number.isNaN(input) ? null : input;
  if (input == null) return null;

  const raw = String(input).trim();
  if (raw === "") return null;

  const { group, decimal } = getSeparators(locale);

  let s = raw.replace(SPACE_RE, "");
  // Strip grouping separators, then convert the decimal separator to ".".
  if (group && group.trim() !== "") s = s.split(group).join("");
  s = s.split(decimal).join(".");

  const n = Number(s);
  return Number.isNaN(n) ? null : n;
}

// Display format: grouped, locale-correct, fixed fraction digits.
export function formatLocaleNumber(
  value,
  { decimals = 2, locale = getActiveLocale() } = {}
) {
  if (value === "" || value == null) return "";
  const n = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(n)) return "";
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n);
}

// True only when `text` is a well-formed number for this locale:
//   - optional grouped integer part (groups of 3 digits) OR a plain integer
//   - at most one decimal separator followed by digits
// A stray dot in a EU locale (e.g. "710.03") is NOT valid grouping, so this
// returns false and drives the visible "Invalid format" error in the input.
export function isValidLocaleNumber(text, locale = getActiveLocale()) {
  if (typeof text === "number") return !Number.isNaN(text);
  if (text == null) return false;

  const raw = String(text).replace(SPACE_RE, "").trim();
  if (raw === "") return false;

  const { group, decimal } = getSeparators(locale);
  const g = escapeRegExp(group);
  const d = escapeRegExp(decimal);

  // integer part: either grouped (1-3 digits + (sep 3-digits)+) or ungrouped digits
  const grouped = `\\d{1,3}(?:${g}\\d{3})+`;
  const plain = `\\d+`;
  const intPart = `(?:${grouped}|${plain})`;
  const decPart = `(?:${d}\\d+)?`;

  const re = new RegExp(`^${intPart}${decPart}$`);
  return re.test(raw);
}
